import { cap } from '../utils/format';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, UserPlus, RotateCcw, History, QrCode, FileUp, FileText, X, Ban, Eye, Receipt, Printer } from 'lucide-react';
import AssetQR from '../components/AssetQR';
import DateRangePicker from '../components/DateRangePicker';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, MultiFilterSelect, PageHeader } from '../components/FilterBar';
import { useMasterValues } from '../hooks/useMasterData';
import WarrantyBadge from '../components/WarrantyBadge';
import { WARRANTY_PERIODS } from '../utils/warranty';
import { DEREC_TYPES, calcWDV, fmtLKR, releasesAssetCustody, blocksAssetAllocation, statusLabel } from '../utils/assetUtils';
import SearchableSelect from '../components/SearchableSelect';
import BulkAssetImport from '../components/BulkAssetImport';
import { BoardApprovalChip } from '../components/BoardApproval';
import { damagePayerLabel, damagePayerColor } from '../utils/damage';

// Short date for the history modal
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// Collapsible-free section wrapper used throughout the full-history modal
function HSection({ label, count, empty, children }) {
  const has = React.Children.count(children) > 0 && count > 0;
  return (
    <div>
      <div className="text-xs font-semibold mb-2" style={{ color: '#94a3b8' }}>
        {label}{count !== undefined ? ` (${count})` : ''}
      </div>
      {has ? children : <div className="text-center py-4 text-xs" style={{ color: '#1e3a5f' }}>{empty}</div>}
    </div>
  );
}

const CATEGORIES = ['mobile_phone','laptop','desktop','printer','scanner','monitor','tv','accessory','networking','server','tablet','smartwatch','other'];
const STATUSES = ['in_use','available','in_repair','broken','lost','donated','eol','disposed','derecognized','in_store'];
const DEP_STATUSES = ['active','depreciated','eol'];
const LAPTOP_CATS = ['laptop','desktop','server'];

function depAge(d) {
  if (!d) return null;
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (days < 30) return `${days}d`;
  if (days < 365) return `${Math.floor(days/30)}mo`;
  const y = Math.floor(days/365), m = Math.floor((days%365)/30);
  return m ? `${y}y ${m}mo` : `${y}y`;
}

// ── Combobox: type or pick from dropdown list ──────────────────────────────
const Combo = ({ label, name, form, setForm, options = [], placeholder }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = filter ? options.filter(o => o.toLowerCase().includes(filter.toLowerCase())) : options;

  return (
    <div ref={ref} className="relative">
      {label && <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>{label}</label>}
      <input
        value={form[name] || ''}
        onChange={e => { setForm({...form, [name]: e.target.value}); setFilter(e.target.value); setOpen(true); }}
        onFocus={() => { setFilter(''); setOpen(true); }}
        placeholder={placeholder || 'Type or select...'}
        className="input-dark"
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden" style={{background:'#0d1528',border:'1px solid rgba(59,130,246,0.3)',boxShadow:'0 8px 32px rgba(0,0,0,0.6)',maxHeight:200,overflowY:'auto'}}>
          {filtered.map(o => (
            <div key={o} onClick={() => { setForm({...form, [name]: o}); setOpen(false); }}
              className="px-3 py-2 text-sm cursor-pointer transition-colors"
              style={{color:'#e2e8f0'}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(59,130,246,0.15)'}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              {o}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function Assets() {
  const { isIT, isAdmin, isViewOnly, user } = useAuth();
  // Admin and IT staff can enter purchaser details and print the sale letter (backend: SALE_DETAIL_ROLES)
  const canEditSale = isAdmin || user?.role === 'it_staff';
  // Master data for dropdowns
  const mobileBrands = useMasterValues('mobile_brand');
  const laptopBrands = useMasterValues('laptop_brand');
  const printerBrands = useMasterValues('printer_brand');
  const vendors = useMasterValues('vendor');
  const osVersions = useMasterValues('os_version');
  const officeVersions = useMasterValues('office_version');
  const ramOptions = useMasterValues('ram');
  const storageOptions = useMasterValues('storage');
  const antivirusOptions = useMasterValues('antivirus');
  const processorOptions = useMasterValues('processor');

  const assignRemarksRef = useRef(null);
  const returnRemarksRef = useRef(null);

  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [category, setCategory] = useState(''); const [status, setStatus] = useState([]);
  const [depStatus, setDepStatus] = useState(''); const [company, setCompany] = useState([]);
  const [derecognized, setDerecognized] = useState('');
  const [derecType, setDerecType] = useState('');
  const [dateFrom, setDateFrom] = useState(''); const [dateTo, setDateTo] = useState('');
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [history, setHistory] = useState([]); const [historyLoading, setHistoryLoading] = useState(false);
  const [qrAsset, setQrAsset] = useState(null); const [qrDetail, setQrDetail] = useState(null);
  const [employees, setEmployees] = useState([]); const [companies, setCompanies] = useState([]); const [departments, setDepartments] = useState([]);
  // Master data for dropdowns
  const [brands, setBrands] = useState([]); const [models, setModels] = useState([]);
  const [idGenerating, setIdGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (search) params.search = search; if (category) params.category = category;
      if (status.length) params.status = status.join(','); if (depStatus) params.depreciation_status = depStatus;
      if (company.length) params.company_id = company.join(',');
      if (derecognized) params.derecognized = derecognized;
      if (derecType) params.derecognition_type = derecType;
      if (dateFrom) params.from = dateFrom; if (dateTo) params.to = dateTo;
      const r = await api.get('/assets', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, category, status, depStatus, company, derecognized, derecType, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/employees', { params: { limit: 1000, status: 'active' } }).then(r => setEmployees(r.data.data || [])).catch(() => {});
    api.get('/settings/companies').then(r => setCompanies(r.data || [])).catch(() => {});
    api.get('/settings/departments').then(r => setDepartments(r.data || [])).catch(() => {});
    // brands now come from master data hooks above
  }, []);

  // Load models when brand changes
  useEffect(() => {
    if (form.brand) {
      api.get('/master/models', { params: { brand: form.brand, category: form.category } })
        .then(r => setModels(r.data || [])).catch(() => {});
    }
  }, [form.brand, form.category]);

  // Auto-generate asset ID
  const generateId = async () => {
    if (!form.category) return;
    setIdGenerating(true);
    try {
      const r = await api.post('/master/generate-asset-id', { category: form.category, brand: form.brand, company_id: form.company_id });
      setForm(prev => ({ ...prev, asset_number: r.data.asset_id }));
    } catch {} finally { setIdGenerating(false); }
  };

  // Auto-generate asset ID whenever category or company changes
  const autoGenerateId = async (category, brand, company_id) => {
    if (!category || !company_id) return;
    try {
      const r = await api.post('/master/generate-asset-id', { category, brand, company_id });
      setForm(prev => ({ ...prev, asset_number: r.data.asset_id }));
    } catch {}
  };

  const openAdd = () => {
    setSelected(null);
    setForm({ category: 'laptop', status: 'available' });
    setError(''); setModal('form');
  };
  const openEdit = async (r) => {
    setSelected(r);
    const full = await api.get(`/assets/${r.id}`).then(x => x.data).catch(() => null);
    const base = full || r;
    // Only pick primitive/FK fields — never spread nested association objects into the form
    const pick = (o, keys) => Object.fromEntries(keys.filter(k => o[k] !== undefined).map(k => [k, o[k]]));
    const ASSET_FIELDS = ['id','asset_number','category','brand','model','serial_number','imei_1','imei_2',
      'mac_address','ip_address','company_id','department_id','current_holder_id','status','purchased_date',
      'purchase_price','purchase_currency','vendor','warranty_period','warranty_expiry_date','warranty_void_reason','location',
      'insured_local','insured_overseas','finance_emails','derecognized','derecognition_date',
      'derecognition_type','derecognition_approved_by','sale_value','remarks','allocation_remarks'];
    const formBase = { ...pick(base, ASSET_FIELDS), current_holder_id: base.currentHolder?.id || base.current_holder_id || '' };
    let cd = {};
    if (LAPTOP_CATS.includes(r.category) && full?.computerDetail) {
      cd = {
        windows_license_key: full.computerDetail.windows_license_key || '',
        office_license_key:  full.computerDetail.office_license_key  || '',
        windows_version:     full.computerDetail.windows_version,
        office_version:      full.computerDetail.office_version,
        ram: full.computerDetail.ram, storage: full.computerDetail.storage,
        processor: full.computerDetail.processor, username: full.computerDetail.username,
        antivirus: full.computerDetail.antivirus
      };
    }
    setForm({ ...formBase, ...cd });
    setError(''); setModal('form');
  };
  const openHistory = async (r) => {
    setSelected(r); setHistoryLoading(true); setModal('history');
    const h = await api.get(`/assets/${r.id}/history`).then(x => x.data)
      .catch(() => ({ assignments: [], repairs: [], damages: [], boardApprovals: [], tickets: [], licenses: [], insuranceClaims: [], donations: [], audit: [] }));
    setHistory(h); setHistoryLoading(false);
  };

  // Open the printable full history in a new tab. The window must be opened
  // synchronously in the click handler — after an await it is popup-blocked.
  const printHistory = (r) => {
    if (!r?.id) return;
    window.open(`/api/assets/${r.id}/history/print?token=${encodeURIComponent(localStorage.getItem('token') || '')}`, '_blank');
  };

  const openQR = async (r) => {
    const full = await api.get(`/assets/${r.id}`).then(x => x.data).catch(() => null);
    setQrAsset(full || r);
    setQrDetail(full?.computerDetail || null);
    setModal('qr');
  };

  const [viewAsset, setViewAsset] = useState(null);
  const [viewDonation, setViewDonation] = useState(null);
  const [showSaleLetter, setShowSaleLetter] = useState(false);
  const [saleForm, setSaleForm] = useState({ purchaser_name: '', purchaser_id_number: '', purchaser_address: '', purchaser_contact: '' });
  const [saleSaving, setSaleSaving] = useState(false);
  const [saleError, setSaleError] = useState('');
  const openView = async (r) => {
    const full = await api.get(`/assets/${r.id}`).then(x => x.data).catch(() => r);
    setViewAsset(full);
    setViewDonation(null);
    setModal('view');
    if (full.derecognized && full.derecognition_type === 'donated') {
      api.get('/donations', { params: { asset_id: full.id, limit: 1 } })
        .then(x => setViewDonation((x.data.data || [])[0] || null)).catch(() => {});
    }
  };

  const handleSave = async e => {
    e.preventDefault();
    // Mandatory field validation
    if (!form.company_id) { setError('Company is required.'); return; }
    if (form.status === 'in_use' && !form.current_holder_id) {
      setError('Holder is required when status is "In Use". Please select an employee.'); return;
    }
    if (!form.serial_number?.trim()) {
      setError('Serial Number is required.'); return;
    }
    if (!form.purchased_date) {
      setError('Purchase Date is required.'); return;
    }
    if (!form.purchase_price) {
      setError('Purchase Price is required.'); return;
    }
    if (!form.warranty_period || form.warranty_period === 'none') {
      setError('Warranty Period is required. Select a period, "Warranty Void", or "Lifetime" if applicable.'); return;
    }
    const isMobile = form.category === 'mobile_phone';
    if (isMobile && !form.imei_1?.trim()) {
      setError('IMEI Number is required for mobile phones.'); return;
    }
    const isComputer = form.category === 'laptop' || form.category === 'desktop';
    if (isComputer && !form.windows_version && !selected) {
      setError('Computer details (Windows version) are required for laptops and desktops.'); return;
    }
    setSaving(true); setError('');
    try {
      if (!selected) await api.post('/assets', form);
      else await api.put(`/assets/${selected.id}`, form);
      setModal(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };
  const handleAssign = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      await api.post(`/assets/${selected.id}/assign`, {
        employee_id: form.employee_id,
        allocation_date: form.allocation_date || new Date().toISOString().slice(0,10),
        remarks: assignRemarksRef.current?.value || ''
      });
      setModal(null); load();
    }
    catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };
  const handleReturn = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try { await api.post(`/assets/${selected.id}/return`, { ...form, remarks: returnRemarksRef.current?.value || '' }); setModal(null); load(); }
    catch (err) { setError(err.response?.data?.error || 'Failed'); } finally { setSaving(false); }
  };

  const isLaptop = LAPTOP_CATS.includes(form.category);

  const columns = [
    { header: 'Asset #', render: r => <span className="font-mono text-xs font-semibold" style={{color:'#60a5fa'}}>{r.asset_number}</span> },
    { header: 'Category', render: r => <Badge value={r.category}/> },
    { header: 'Brand / Model', render: r => <div><div className="font-medium text-sm" style={{color:'#e2e8f0'}}>{r.brand||'—'}</div><div className="text-xs" style={{color:'#334155'}}>{r.model||''}</div></div> },
    { header: 'Serial #', render: r => <span className="font-mono text-xs" style={{color:'#475569'}}>{r.serial_number||'—'}</span> },
    { header: 'Holder', render: r => r.currentHolder
      ? <div><div className="text-xs font-medium" style={{color:'#94a3b8'}}>{r.currentHolder.full_name}</div></div>
      // Nobody holds it now — show who used it last (returned, released or derecognized)
      : r.lastHolder
        ? <div>
            <div className="text-xs" style={{color:'#64748b'}}>Last used by</div>
            <div className="text-xs font-medium" style={{color:'#94a3b8'}}>{r.lastHolder.full_name}</div>
            {r.last_held_to && <div className="text-xs" style={{color:'#475569'}}>until {new Date(r.last_held_to).toLocaleDateString('en-GB')}</div>}
          </div>
        : <span style={{color:'#1e3a5f'}}>—</span>
    },
    { header: 'Status', render: r => <div className="space-y-1"><Badge value={r.status}/>{r.derecognized && r.status !== 'derecognized' && <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(239,68,68,0.15)',color:'#f87171'}}><Ban size={9}/>Derecognized</span>}{r.insured_local && <span className="block text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(59,130,246,0.15)',color:'#60a5fa'}}>Local Ins.</span>}{r.insured_overseas && <span className="block text-xs px-1.5 py-0.5 rounded" style={{background:'rgba(139,92,246,0.15)',color:'#a78bfa'}}>Overseas Ins.</span>}</div> },
    { header: 'Age / WDV', render: r => {
      // For derecognized assets: freeze all depreciation at derecognition_date
      const isD = !!r.derecognized;
      const frozenWDV  = isD ? calcWDV(r.purchase_price, r.purchased_date, r.derecognition_date) : null;
      const frozenDep  = isD && r.purchase_price && frozenWDV != null ? Math.max(0, parseFloat(r.purchase_price) - frozenWDV) : null;
      const displayWDV = isD ? frozenWDV : (r.wdv != null ? r.wdv : calcWDV(r.purchase_price, r.purchased_date));
      const displayDep = isD ? frozenDep : r.depreciated_value;
      const daysRemaining = isD ? null : r.wdv_days_remaining;
      // Depreciation badge: derecognized assets never show "Depreciating"
      const depLabel = isD ? null
        : r.depreciation_status === 'active' ? 'Depreciating'
        : r.depreciation_status === 'depreciated' ? 'Fully Depr\'d' : null;
      // Gain/loss
      const saleVal  = r.sale_value != null ? parseFloat(r.sale_value) : null;
      const gainLoss = isD && r.derecognition_type === 'sold' && saleVal != null && frozenWDV != null
        ? saleVal - frozenWDV : null;
      return (
        <div>
          {depLabel && <Badge value={r.depreciation_status} label={depLabel}/>}
          {r.age_days != null
            ? <div className="text-xs mt-0.5" style={{color:'#94a3b8'}}><span style={{color:'#475569'}}>Age: </span>{r.age_days >= 365 ? `${Math.floor(r.age_days/365)}y ${Math.floor((r.age_days%365)/30)}mo` : `${Math.floor(r.age_days/30)}mo`} ({r.age_days}d)</div>
            : r.purchased_date && <div className="text-xs mt-0.5" style={{color:'#94a3b8'}}><span style={{color:'#475569'}}>Age: </span>{depAge(r.purchased_date)}</div>
          }
          {displayWDV != null && <div className="text-xs mt-0.5 font-mono" style={{color: displayWDV === 0 ? '#ef4444' : '#34d399'}}><span style={{color:'#475569',fontFamily:'inherit'}}>WDV: </span>{fmtLKR(displayWDV)}</div>}
          {displayDep != null && <div className="text-xs mt-0.5 font-mono" style={{color:'#f97316'}}><span style={{color:'#475569',fontFamily:'inherit'}}>Accum. Dep: </span>{fmtLKR(displayDep)}</div>}
          {daysRemaining != null && daysRemaining > 0 && <div className="text-xs mt-0.5" style={{color:'#64748b'}}><span style={{color:'#475569'}}>Deprn. left: </span>{daysRemaining}d</div>}
          {/* Disposal summary — only for derecognized */}
          {isD && frozenWDV != null && (
            <div className="mt-1 pt-1" style={{borderTop:'1px solid rgba(255,255,255,0.06)'}}>
              {r.derecognition_type === 'sold' && saleVal != null && (
                <div className="text-xs font-mono" style={{color:'#94a3b8'}}><span style={{color:'#475569',fontFamily:'inherit'}}>Sale Value: </span>{fmtLKR(saleVal)}</div>
              )}
              {gainLoss != null && (
                <div className="text-xs font-mono font-semibold" style={{color: gainLoss >= 0 ? '#34d399' : '#f87171'}}>
                  {gainLoss >= 0 ? '▲ Gain: ' : '▼ Loss: '}{fmtLKR(Math.abs(gainLoss))}
                </div>
              )}
              {r.derecognition_type !== 'sold' && frozenWDV > 0 && (
                <div className="text-xs font-mono font-semibold" style={{color:'#f87171'}}>▼ Written Off: {fmtLKR(frozenWDV)}</div>
              )}
            </div>
          )}
          {r.warranty_period && r.warranty_period !== 'none' && (
            <div className="mt-0.5 flex items-center gap-1">
              <span className="text-xs" style={{color:'#475569'}}>Warranty:</span>
              <WarrantyBadge warrantyPeriod={r.warranty_period} purchasedDate={r.purchased_date} small/>
            </div>
          )}
        </div>
      );
    }},
    { header: 'Co.', render: r => <span className="text-xs font-mono" style={{color:'#334155'}}>{r.Company?.code||'—'}</span> },
    { header: '', render: r => (
      <div className="flex items-center gap-0.5">
        <button onClick={() => openView(r)} className="btn-ghost px-1.5 py-1" title="View Details" style={{color:'#60a5fa'}}><Eye size={11}/></button>
        {(isAdmin || (isIT && !r.derecognized)) && !isViewOnly && <button onClick={() => openEdit(r)} className="btn-ghost px-1.5 py-1" title={r.derecognized ? 'Edit (admin only — derecognized)' : 'Edit'}><Edit2 size={11}/></button>}
        {isIT && !isViewOnly && <button onClick={() => openHistory(r)} className="btn-ghost px-1.5 py-1" title="History"><History size={11}/></button>}
        {!isViewOnly && !r.derecognized && <button onClick={() => openQR(r)} className="btn-ghost px-1.5 py-1" title="QR Code" style={{color:'#818cf8'}}><QrCode size={11}/></button>}
        {isIT && !isViewOnly && !r.current_holder_id && !r.derecognized && !blocksAssetAllocation(r.status) && <button onClick={() => { setSelected(r); setForm({remarks:''}); setError(''); setModal('assign'); }} className="btn-ghost px-1.5 py-1" style={{color:'#10b981'}}><UserPlus size={11}/></button>}
        {isIT && !isViewOnly && r.current_holder_id && <button onClick={() => { setSelected(r); setForm({condition:'good',remarks:'',mark_as:'available'}); setError(''); setModal('return'); }} className="btn-ghost px-1.5 py-1" style={{color:'#f59e0b'}}><RotateCcw size={11}/></button>}
        {isAdmin && <button onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`/assets/${r.id}`); load(); }}} className="btn-ghost px-1.5 py-1" style={{color:'#ef4444'}}><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="All Assets" count={total}>
        <button onClick={load} className="btn-ghost"><RefreshCw size={13}/></button>
        {isIT && <BulkAssetImport onImported={load}/>}
        {isIT && <button onClick={openAdd} className="btn-primary"><Plus size={13}/> Add Asset</button>}
      </PageHeader>

      <FilterBar hasFilters={!!(search||category||status.length||depStatus||company.length||derecognized||derecType||dateFrom||dateTo)} onClear={() => { setSearch(''); setCategory(''); setStatus([]); setDepStatus(''); setCompany([]); setDerecognized(''); setDerecType(''); setDateFrom(''); setDateTo(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search asset #, brand, model, serial, IMEI..."/>
        <FilterSelect value={category} onChange={v => { setCategory(v); setPage(1); }} placeholder="All Categories" options={CATEGORIES.map(c=>({value:c,label:c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}))}/>
        <MultiFilterSelect value={status} onChange={v => { setStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s=>({value:s,label:s.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}))}/>
        <FilterSelect value={depStatus} onChange={v => { setDepStatus(v); setPage(1); }} placeholder="Depreciation: All" options={DEP_STATUSES.map(s=>({value:s,label:s}))}/>
        <FilterSelect value={derecognized} onChange={v => { setDerecognized(v); setDerecType(''); setPage(1); }} placeholder="Derecognition: All" options={[{value:'false',label:'Active Only'},{value:'true',label:'Derecognized Only'},{value:'eol_pending',label:'EOL — Pending Derecognition'}]}/>
        <FilterSelect value={derecType} onChange={v => { setDerecType(v); setDerecognized(''); setPage(1); }} placeholder="Disposal Type: All" options={DEREC_TYPES.map(t=>({value:t,label:t.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())}))}/>
        <MultiFilterSelect value={company} onChange={v => { setCompany(v); setPage(1); }} placeholder="All Companies" options={companies.map(c=>({value:c.id,label:c.name}))}/>
        <DateRangePicker from={dateFrom} to={dateTo} onChange={(f,t) => { setDateFrom(f); setDateTo(t); setPage(1); }}/>
      </FilterBar>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {/* ADD / EDIT */}
      {modal === 'form' && (
        <Modal title={selected ? `Edit: ${selected.asset_number}` : 'Add Asset'} onClose={() => setModal(null)} size="xl">
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>

            {/* Asset ID — READ ONLY, auto-generated by system */}
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>
                Asset Number {selected ? '' : '— auto-generated'}
              </label>
              <div className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{background:'rgba(99,102,241,0.08)',border:'1px solid rgba(99,102,241,0.2)'}}>
                <span className="font-mono font-semibold flex-1" style={{color: form.asset_number ? '#818cf8' : '#334155', fontSize: 13}}>
                  {form.asset_number || 'Select Category + Company → ID auto-generated'}
                </span>
                {idGenerating && <span className="text-xs" style={{color:'#334155'}}>Generating...</span>}
              </div>
              <div className="text-xs mt-1" style={{color:'#1e3a5f'}}>Format: PREFIX/COMPANY/YEAR/MONTH/BRAND/L0001 — unique per company</div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Category *</label>
                <select required value={form.category||''} onChange={e => {
                  const newForm = {...form, category: e.target.value, brand:'', model:''};
                  setForm(newForm);
                  if (newForm.company_id) autoGenerateId(e.target.value, '', newForm.company_id);
                }} className="input-dark">
                  <option value="">Select...</option>
                  {CATEGORIES.map(c=><option key={c} value={c}>{c.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Status</label>
                <select value={form.status||'available'} onChange={e=>setForm({...form,status:e.target.value})} className="input-dark">
                  {STATUSES.map(s=><option key={s} value={s}>{s.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
                </select>
                {releasesAssetCustody(form.status) && selected?.current_holder_id && (
                  <div className="text-xs mt-1" style={{color:'#fbbf24'}}>
                    Saving will release this asset from <strong>{selected.currentHolder?.full_name || 'its current holder'}</strong> — it can no longer be allocated while marked {statusLabel(form.status)}.
                  </div>
                )}
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Company *</label>
                <SearchableSelect required value={form.company_id||''} onChange={v => {
                  const newForm = {...form, company_id: v, department_id: ''};
                  setForm(newForm);
                  if (newForm.category) autoGenerateId(newForm.category, newForm.brand, v);
                }} options={companies.map(c=>({value:c.id,label:c.name}))} placeholder="Select company"/>
              </div>
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Department</label>
                <SearchableSelect value={form.department_id||''} onChange={v=>setForm({...form,department_id:v||null})}
                  options={(form.company_id ? departments.filter(d=>d.company_id===form.company_id) : departments).map(d=>({value:d.id,label:d.name}))}
                  placeholder="— No department —"/>
              </div>

              {/* Brand from master data */}
              <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Brand</label>
                <select value={form.brand||''} onChange={e => {
                  const newBrand = e.target.value;
                  const isLaptopCat = LAPTOP_CATS.includes(form.category);
                  let autoFill = {};
                  if (isLaptopCat && newBrand) {
                    if (newBrand.toLowerCase() === 'apple') {
                      autoFill = { windows_version: 'macOS', ram: '16GB', storage: '256GB', antivirus: 'Sophos', processor: 'M4', warranty_period: '1_year' };
                    } else {
                      autoFill = { windows_version: 'Windows 11 Pro', ram: '16GB', storage: '512GB', antivirus: 'Sophos', processor: 'Intel Core Ultra 5', warranty_period: '1_year' };
                    }
                  }
                  setForm({...form, brand: newBrand, model:'', ...autoFill});
                  if (form.category && form.company_id) autoGenerateId(form.category, newBrand, form.company_id);
                }} className="input-dark">
                  <option value="">Select brand</option>
                  {(['mobile_phone','tablet','smartwatch'].includes(form.category) ? mobileBrands :
                    ['laptop','desktop','server'].includes(form.category) ? laptopBrands :
                    ['printer','scanner'].includes(form.category) ? printerBrands :
                    [...mobileBrands,...laptopBrands,...printerBrands]
                  ).map(b=><option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </div>
              <Combo label="Model" name="model" form={form} setForm={setForm} options={models} placeholder="Type or pick model..."/>
              <FF label="Serial Number *" name="serial_number" form={form} setForm={setForm} required/>

              {(form.category==='mobile_phone'||form.category==='tablet') && <>
                <FF label="IMEI 1" name="imei_1" form={form} setForm={setForm}/>
                <FF label="IMEI 2" name="imei_2" form={form} setForm={setForm}/>
              </>}

              <div>
                <label className="block text-xs font-medium mb-1" style={{color: form.status === 'in_use' ? '#ef4444' : '#475569'}}>
                  Holder (Current User){form.status === 'in_use' ? ' *' : ''}
                  {form.status === 'in_use' && !form.current_holder_id && <span className="ml-2 text-xs font-normal" style={{color:'#ef4444'}}>Required when status is In Use</span>}
                </label>
                {/* Warn if current holder is resigned (not in active employees list) */}
                {form.current_holder_id && !employees.find(e => e.id === form.current_holder_id) && selected?.currentHolder && (
                  <div className="mb-1.5 px-3 py-1.5 rounded-lg text-xs flex items-center gap-2" style={{background:'rgba(251,146,60,0.1)',border:'1px solid rgba(251,146,60,0.3)',color:'#fb923c'}}>
                    ⚠ <strong>{selected.currentHolder.full_name}</strong> has resigned — please reassign to an active employee
                  </div>
                )}
                <SearchableSelect value={form.current_holder_id||''}
                  onChange={v => setForm({...form, current_holder_id: v})}
                  style={{borderColor: form.status === 'in_use' && !form.current_holder_id ? '#ef4444' : ''}}
                  placeholder="Unassigned / Available"
                  options={[
                    // A resigned holder is no longer in the active list — keep them
                    // visible so it is clear who the asset is still charged to
                    ...(form.current_holder_id && !employees.find(e => e.id === form.current_holder_id) && selected?.currentHolder
                      ? [{ value: form.current_holder_id, label: `⚠ ${selected.currentHolder.full_name}`, hint: 'Resigned — please reassign' }]
                      : []),
                    ...employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined })),
                  ]}/>
              </div>

              <FF label="Purchase Date *" name="purchased_date" type="date" form={form} setForm={setForm} required/>
              <FF label="Purchase Price (LKR) *" name="purchase_price" type="number" form={form} setForm={setForm} required/>
              <div>
                <label className="block text-xs font-medium mb-1" style={{color: (!form.warranty_period || form.warranty_period === 'none') ? '#ef4444' : '#475569'}}>
                  Warranty Period *
                </label>
                <select required value={form.warranty_period||'none'} onChange={e=>setForm({...form,warranty_period:e.target.value})}
                  className="input-dark"
                  style={{borderColor: (!form.warranty_period || form.warranty_period === 'none') ? '#ef4444' : undefined}}>
                  {WARRANTY_PERIODS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
                {form.warranty_period && form.warranty_period !== 'none' && form.purchased_date && (
                  <div className="mt-1.5"><WarrantyBadge warrantyPeriod={form.warranty_period} purchasedDate={form.purchased_date}/></div>
                )}
                {form.warranty_period === 'warranty_void' && (
                  <div className="mt-2">
                    <label className="block text-xs font-medium mb-1" style={{color:'#fbbf24'}}>Why was the warranty voided?</label>
                    <textarea rows={2} value={form.warranty_void_reason||''}
                      onChange={e=>setForm({...form,warranty_void_reason:e.target.value})}
                      className="input-dark" placeholder="e.g. Liquid damage (tea spill) — not covered by manufacturer warranty"/>
                    <div className="text-xs mt-1" style={{color:'#334155'}}>Recorded on the asset so anyone can see why later.</div>
                  </div>
                )}
              </div>
              <Combo label="Vendor" name="vendor" form={form} setForm={setForm} options={vendors.map(v=>v.value)} placeholder="Type or pick vendor..."/>
              <FF label="Location" name="location" form={form} setForm={setForm} placeholder="e.g. Head Office, Floor 2"/>
              {/* Insurance coverage */}
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-2" style={{color:'#475569'}}>Insurance Coverage</label>
                <div className="flex gap-6 mb-3">
                  <label className="flex items-center gap-2 cursor-pointer text-sm" style={{color:'#e2e8f0'}}>
                    <input type="checkbox" checked={!!form.insured_local} onChange={e=>setForm({...form,insured_local:e.target.checked})} className="accent-blue-500 w-4 h-4"/>
                    Local Insurance
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-sm" style={{color:'#e2e8f0'}}>
                    <input type="checkbox" checked={!!form.insured_overseas} onChange={e=>setForm({...form,insured_overseas:e.target.checked})} className="accent-purple-500 w-4 h-4"/>
                    Overseas Insurance
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#f59e0b'}}>
                    Finance Team Email(s) for Insurance Notification
                    <span className="ml-1 font-normal" style={{color:'#475569'}}>(comma-separated — email with invoice sent when invoice is uploaded)</span>
                  </label>
                  <input
                    type="text"
                    value={form.finance_emails||''}
                    onChange={e=>setForm({...form,finance_emails:e.target.value})}
                    className="input-dark"
                    placeholder="finance@clsynergy.com, insurance@clsynergy.com"
                  />
                </div>
              </div>
              {(form.category==='printer'||form.category==='scanner') && <>
                <FF label="Network / IP Address" name="ip_address" form={form} setForm={setForm}/>
                <FF label="MAC Address" name="mac_address" form={form} setForm={setForm}/>
              </>}
            </div>

            {/* Laptop / Desktop section */}
            {isLaptop && (
              <div className="rounded-xl p-4 space-y-3" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.2)'}}>
                <div className="text-xs font-semibold" style={{color:'#60a5fa'}}>💻 Computer Details — license keys auto-create in License module</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Windows Version</label>
                    <select value={form.windows_version||''} onChange={e=>setForm({...form,windows_version:e.target.value})} className="input-dark">
                      <option value="">Select or type below</option>
                      {osVersions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <FF label="Windows License Key" name="windows_license_key" form={form} setForm={setForm} placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"/>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Office Version</label>
                    <select value={form.office_version||''} onChange={e=>setForm({...form,office_version:e.target.value})} className="input-dark">
                      <option value="">Select version</option>
                      {officeVersions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select>
                  </div>
                  <FF label="Office License Key" name="office_license_key" form={form} setForm={setForm} placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"/>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>RAM</label>
                    <select value={form.ram||''} onChange={e=>setForm({...form,ram:e.target.value})} className="input-dark">
                      <option value="">Select RAM</option>
                      {ramOptions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select></div>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Storage</label>
                    <select value={form.storage||''} onChange={e=>setForm({...form,storage:e.target.value})} className="input-dark">
                      <option value="">Select Storage</option>
                      {storageOptions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select></div>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Processor</label>
                    <select value={form.processor||''} onChange={e=>setForm({...form,processor:e.target.value})} className="input-dark">
                      <option value="">Select Processor</option>
                      {processorOptions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select></div>
                  <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Antivirus</label>
                    <select value={form.antivirus||''} onChange={e=>setForm({...form,antivirus:e.target.value})} className="input-dark">
                      <option value="">Select Antivirus</option>
                      {antivirusOptions.map(v=><option key={v.value} value={v.value}>{v.label}</option>)}
                    </select></div>
                  <FF label="Login Username" name="username" form={form} setForm={setForm} placeholder="Windows login username"/>
                  <FF label="IP Address" name="ip_address" form={form} setForm={setForm} placeholder="DHCP or static IP"/>
                  <FF label="MAC Address" name="mac_address" form={form} setForm={setForm}/>
                </div>
              </div>
            )}

            {/* Derecognition */}
            <div className="rounded-xl p-4" style={{background:'rgba(239,68,68,0.04)',border:'1px solid rgba(239,68,68,0.2)'}}>
              <div className="text-xs font-semibold mb-3 flex items-center gap-1.5" style={{color:'#f87171'}}><Ban size={11}/> Asset Derecognition</div>
              <label className="flex items-center gap-2 cursor-pointer text-sm mb-3" style={{color:'#e2e8f0'}}>
                <input type="checkbox" checked={!!form.derecognized} onChange={e=>setForm({...form,derecognized:e.target.checked})} className="accent-red-500 w-4 h-4"/>
                Mark as Derecognized — asset remains in register with Derecognized status
              </label>
              {form.derecognized && (
                <div className="grid grid-cols-3 gap-3 mt-1">
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Disposal Type</label>
                    <select value={form.derecognition_type||''} onChange={e=>setForm({...form,derecognition_type:e.target.value})} className="input-dark">
                      <option value="">Select type...</option>
                      {DEREC_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ').replace(/\b\w/g,l=>l.toUpperCase())}</option>)}
                    </select>
                  </div>
                  <FF label="Disposal Date" name="derecognition_date" type="date" form={form} setForm={setForm}/>
                  <FF label="Approved By *" name="derecognition_approved_by" form={form} setForm={setForm} placeholder="Name(s) of approving person(s)"/>
                  {form.derecognition_type === 'sold' && (
                    <FF label="Sale Value (LKR)" name="sale_value" type="number" form={form} setForm={setForm} placeholder="0"/>
                  )}
                  {form.purchase_price && form.purchased_date && (
                    <div className="col-span-3 p-3 rounded-lg" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)'}}>
                      <div className="text-xs font-semibold mb-1" style={{color:'#94a3b8'}}>Accounting Summary at Disposal</div>
                      <div className="grid grid-cols-3 gap-3 text-xs">
                        <div><span style={{color:'#475569'}}>Purchase Price: </span><span style={{color:'#e2e8f0'}}>{fmtLKR(form.purchase_price)}</span></div>
                        <div><span style={{color:'#475569'}}>WDV at Disposal: </span><span style={{color:'#34d399'}}>{fmtLKR(calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date))}</span></div>
                        {form.derecognition_type === 'sold' && form.sale_value != null && form.sale_value !== '' && (
                          <div>
                            <span style={{color:'#475569'}}>Gain / Loss: </span>
                            <span style={{color: (parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date)) >= 0 ? '#34d399' : '#f87171', fontWeight:700}}>
                              {fmtLKR(parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date))}
                              {' '}{(parseFloat(form.sale_value) - calcWDV(form.purchase_price, form.purchased_date, form.derecognition_date)) >= 0 ? '(Gain)' : '(Loss)'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={2}/>

            {/* Supplier Invoice */}
            <div className="rounded-xl p-4" style={{background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.2)'}}>
              <div className="text-xs font-semibold mb-2 flex items-center gap-2" style={{color:'#fbbf24'}}><FileUp size={12}/> Supplier Invoice</div>
              {selected?.invoice_filename ? (
                <div className="flex items-center gap-2">
                  <FileText size={13} style={{color:'#fbbf24'}}/>
                  <a href={selected.invoice_url} target="_blank" rel="noreferrer" className="text-xs underline" style={{color:'#60a5fa'}}>{selected.invoice_filename}</a>
                  <span className="text-xs" style={{color:'#334155'}}>(click to view/download)</span>
                  {isIT && <button type="button" onClick={async () => { await api.delete(`/assets/${selected.id}/invoice`); load(); setModal(null); }} className="ml-auto text-xs px-2 py-0.5 rounded" style={{color:'#ef4444',background:'rgba(239,68,68,0.1)'}}><X size={11}/> Remove</button>}
                </div>
              ) : (
                <div className="text-xs" style={{color:'#334155'}}>No invoice uploaded yet.</div>
              )}
              {isIT && selected?.id && (
                <div className="mt-2">
                  <label className="text-xs" style={{color:'#475569'}}>Upload Invoice (PDF, image — max 10MB)</label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="mt-1 w-full text-xs" style={{color:'#94a3b8'}}
                    onChange={async e => {
                      if (!e.target.files[0]) return;
                      const fd = new FormData(); fd.append('invoice', e.target.files[0]);
                      await api.post(`/assets/${selected.id}/invoice`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      load(); setModal(null);
                    }}/>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save Asset'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ASSIGN */}
      {modal === 'assign' && (
        <Modal title={`Assign: ${selected?.asset_number}`} onClose={() => setModal(null)}>
          <form onSubmit={handleAssign} className="space-y-4">
            <ErrMsg msg={error}/>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Assign To *</label>
              <SearchableSelect required value={form.employee_id||''}
                onChange={v => setForm({...form, employee_id: v})}
                options={employees.map(e => ({ value: e.id, label: e.full_name, hint: e.employee_code || undefined }))} placeholder="Select employee"/>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Allocation Date *</label>
              <input type="date" required value={form.allocation_date||new Date().toISOString().slice(0,10)} onChange={e=>setForm({...form,allocation_date:e.target.value})} className="input-dark"/>
              <div className="text-xs mt-1" style={{color:'#334155'}}>Recorded in employee profile and asset history.</div>
            </div>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
              <textarea ref={assignRemarksRef} defaultValue="" rows={2} className="input-dark"/></div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" style={{background:'linear-gradient(135deg,#10b981,#059669)'}}>{saving?'...':'Assign'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* RETURN */}
      {modal === 'return' && (
        <Modal title={`Return: ${selected?.asset_number} from ${selected?.currentHolder?.full_name}`} onClose={() => setModal(null)}>
          <form onSubmit={handleReturn} className="space-y-4">
            <ErrMsg msg={error}/>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Mark As</label>
              <select value={form.mark_as||'available'} onChange={e=>setForm({...form,mark_as:e.target.value})} className="input-dark">
                <option value="available">Available (returned)</option>
                <option value="broken">Broken</option>
                <option value="lost">Lost</option>
              </select>
            </div>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Condition</label>
              <select value={form.condition||'good'} onChange={e=>setForm({...form,condition:e.target.value})} className="input-dark">
                {['good','fair','poor','damaged'].map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div><label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Remarks</label>
              <textarea ref={returnRemarksRef} defaultValue="" rows={2} className="input-dark"/></div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" style={{background:'linear-gradient(135deg,#f59e0b,#d97706)'}}>{saving?'...':'Return Asset'}</button>
            </div>
          </form>
        </Modal>
      )}

      {/* QR CODE */}
      {modal === 'qr' && qrAsset && (
        <Modal title={`QR Code — ${qrAsset.asset_number}`} onClose={() => { setModal(null); setQrAsset(null); setQrDetail(null); }} size="md">
          <AssetQR
            asset={qrAsset}
            computerDetail={qrDetail}
            canDownload={isIT}
            onClose={() => { setModal(null); setQrAsset(null); setQrDetail(null); }}
          />
        </Modal>
      )}

      {/* HISTORY */}
      {modal === 'history' && (
        <Modal
          title={`Full History — ${selected?.asset_number} | Purchased: ${selected?.purchased_date||'N/A'}`}
          onClose={() => { setModal(null); setHistory([]); }} size="lg">
          {historyLoading ? (
            <div className="flex items-center justify-center h-24 gap-2" style={{color:'#334155'}}>
              <div className="w-4 h-4 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"/>Loading...
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 pb-3" style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                <div className="text-xs" style={{color:'#475569'}}>
                  Complete lifecycle record — allocations, tickets, repairs, damages, approvals, licenses, insurance and every change.
                </div>
                <button onClick={() => printHistory(selected)} className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap">
                  <Printer size={12}/>&nbsp;Print / Save as PDF
                </button>
              </div>
              <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">

              {/* Who had it before the person holding it now — the first thing
                  anyone looks for in an asset's history */}
              <HSection label="Previous Users" count={(history.previousUsers||[]).length}
                empty={history.asset?.holder_name
                  ? `No earlier holder on record — this asset has been with ${history.asset.holder_name} since it was registered`
                  : 'No earlier holder on record'}>
                {(history.previousUsers||[]).map((u, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-xl mb-2"
                    style={{background:'rgba(139,92,246,0.06)',border:'1px solid rgba(139,92,246,0.18)'}}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{background:'rgba(139,92,246,0.18)',color:'#a78bfa'}}>
                      {u.employee_name?.charAt(0)||'?'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm" style={{color:'#e2e8f0'}}>
                        {u.employee_name}
                        {u.employee_code && <span className="text-xs ml-1.5" style={{color:'#475569'}}>{u.employee_code}</span>}
                        {u.designation && <span className="text-xs ml-1.5" style={{color:'#334155'}}>· {u.designation}</span>}
                      </div>
                      <div className="text-xs mt-0.5" style={{color:'#475569'}}>
                        Held {u.from || '—'}{u.to ? ` → ${u.to}` : ' → (end not recorded)'}
                        {u.ended && <span style={{color:'#334155'}}> · {u.ended}</span>}
                      </div>
                      {u.notes?.length > 0 && <div className="text-xs mt-0.5" style={{color:'#334155'}}>{u.notes.join(' · ')}</div>}
                    </div>
                  </div>
                ))}
                {/* Older assets often name the earlier user only in a typed note */}
                {(history.previousUsers||[]).length === 0 && (history.assignments||[]).some(x => x.remarks) && (
                  <div className="p-3 rounded-xl mt-2" style={{background:'rgba(245,158,11,0.06)',border:'1px solid rgba(245,158,11,0.2)'}}>
                    <div className="text-xs font-semibold mb-1" style={{color:'#fbbf24'}}>Notes on its allocation — these may name an earlier user</div>
                    {(history.assignments||[]).filter(x => x.remarks).map((x, i) => (
                      <div key={i} className="text-xs" style={{color:'#94a3b8'}}>
                        “{x.remarks}” <span style={{color:'#334155'}}>— noted against {x.employee_name || 'an allocation'}{x.assigned_date ? `, ${x.assigned_date}` : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
              </HSection>

              <HSection label="Allocation History" count={(history.assignments||[]).length} empty="Never allocated">
                {(history.assignments||[]).map((h, i) => (
                  <div key={h.id||i} className="flex items-start gap-3 p-3 rounded-xl mb-2" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                      style={{background:'linear-gradient(135deg,rgba(59,130,246,0.2),rgba(99,102,241,0.2))',color:'#60a5fa'}}>
                      {h.employee_name?.charAt(0)||'?'}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-sm" style={{color:'#e2e8f0'}}>
                        {h.employee_name||'—'}
                        {h.employee_code && <span className="text-xs ml-1.5" style={{color:'#475569'}}>{h.employee_code}</span>}
                      </div>
                      <div className="text-xs mt-0.5" style={{color:'#475569'}}>
                        <span style={{color:'#34d399'}}>Handed over: {h.assigned_date||'—'}</span>
                        {h.returned_date && <span style={{color:'#f59e0b'}}> → Returned: {h.returned_date}</span>}
                        {!h.returned_date && <span style={{color:'#60a5fa'}}> → Currently with this person</span>}
                        {h.handled_by && <span style={{color:'#334155'}}> · by {h.handled_by}</span>}
                      </div>
                      {h.condition_at_assign && <div className="text-xs mt-0.5" style={{color:'#334155'}}>Condition out: {h.condition_at_assign}</div>}
                      {h.condition_at_return && <div className="text-xs mt-0.5" style={{color:'#334155'}}>Condition in: {h.condition_at_return}</div>}
                      {h.remarks && <div className="text-xs mt-0.5" style={{color:'#334155'}}>{h.remarks}</div>}
                    </div>
                    <Badge value={h.returned_date ? 'available' : 'in_use'}/>
                  </div>
                ))}
              </HSection>

              <HSection label="IT Tickets" count={(history.tickets||[]).length} empty="No tickets raised against this asset">
                {(history.tickets||[]).map((t, i) => (
                  <div key={t.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(59,130,246,0.05)',border:'1px solid rgba(59,130,246,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}><span className="font-mono" style={{color:'#60a5fa'}}>{t.ticket_number}</span> — {t.title||'—'}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge value={t.status}/>
                        <BoardApprovalChip status={t.board_approval_status}/>
                      </div>
                    </div>
                    <div className="mt-1" style={{color:'#94a3b8'}}>
                      {cap(t.category)} · {cap(t.priority)} · Raised {fmtD(t.reported_date)}
                      {t.reported_by_name ? ` by ${t.reported_by_name}` : ''}
                      {t.assigned_to_name ? ` · Assigned to ${t.assigned_to_name}` : ''}
                      {t.resolved_date ? ` · Resolved ${fmtD(t.resolved_date)}` : ''}
                    </div>
                    {t.resolution_notes && <div className="mt-1" style={{color:'#334155'}}>{t.resolution_notes}</div>}
                  </div>
                ))}
              </HSection>

              <HSection label="Repairs" count={(history.repairs||[]).length} empty="No repair records">
                {(history.repairs||[]).map((r, i) => (
                  <div key={r.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(245,158,11,0.05)',border:'1px solid rgba(245,158,11,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}>{r.issue_description||'—'}</span>
                      <Badge value={r.status}/>
                    </div>
                    <div className="mt-1" style={{color:'#94a3b8'}}>
                      {r.repair_vendor && <>Vendor: {r.repair_vendor} · </>}
                      {r.repair_cost ? `${fmtLKR(r.repair_cost)} · ` : ''}
                      {r.warranty_covered ? 'Under warranty · ' : ''}
                      Reported: {fmtD(r.reported_date)}{r.completed_date ? ` · Completed: ${fmtD(r.completed_date)}` : ''}
                    </div>
                    {r.resolution && <div className="mt-1" style={{color:'#334155'}}>{r.resolution}</div>}
                  </div>
                ))}
              </HSection>

              <HSection label="Damage Records" count={(history.damages||[]).length} empty="No damage records">
                {(history.damages||[]).map((d, i) => (
                  <div key={d.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}>{d.employee_name||'—'} — {d.damage_type||'Damage'}</span>
                      <Badge value={d.payment_status}/>
                    </div>
                    <div className="mt-1" style={{color:'#94a3b8'}}>
                      {fmtLKR(d.repair_cost)} · <span style={{color:damagePayerColor(d.payment_method)}}>{damagePayerLabel(d.payment_method)}</span>
                      {' · '}Incident: {fmtD(d.incident_date)}
                      {d.ticket_number ? ` · ${d.ticket_number}` : ''}
                    </div>
                    {d.damage_description && <div className="mt-1" style={{color:'#334155'}}>{d.damage_description}</div>}
                  </div>
                ))}
              </HSection>

              <HSection label="Board Approvals" count={(history.boardApprovals||[]).length} empty="No board approval requests">
                {(history.boardApprovals||[]).map((b, i) => (
                  <div key={i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(124,58,237,0.05)',border:'1px solid rgba(124,58,237,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}>{b.ticket_number} — {b.ticket_title}</span>
                      <Badge value={b.status}/>
                    </div>
                    <div className="mt-1" style={{color:'#94a3b8'}}>
                      {fmtLKR(b.estimation_cost)}
                      {b.requested_by_name ? ` · by ${b.requested_by_name}` : ''}
                      {b.reminder_count ? ` · ${b.reminder_count} reminder${b.reminder_count > 1 ? 's' : ''}` : ''}
                      {b.decision_at ? ` · Decided: ${new Date(b.decision_at).toLocaleString('en-GB')}` : ` · Requested: ${new Date(b.created_at).toLocaleString('en-GB')}`}
                    </div>
                    {b.admin_remark && <div className="mt-1" style={{color:'#334155'}}>Remark: {b.admin_remark}</div>}
                    {b.rejection_reason && <div className="mt-1" style={{color:'#f87171'}}>Reason: {b.rejection_reason}</div>}
                  </div>
                ))}
              </HSection>

              <HSection label="Licenses" count={(history.licenses||[]).length} empty="No licenses linked">
                {(history.licenses||[]).map((l, i) => (
                  <div key={l.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(16,185,129,0.05)',border:'1px solid rgba(16,185,129,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}>{l.product_name||'—'} <span style={{color:'#475569'}}>({cap(l.license_type)})</span></span>
                      <Badge value={l.status}/>
                    </div>
                    <div className="mt-1 font-mono" style={{color:'#94a3b8'}}>{l.license_key||'—'}</div>
                    <div className="mt-1" style={{color:'#475569'}}>
                      Purchased: {fmtD(l.purchased_date)}{l.expiry_date ? ` · Expires: ${fmtD(l.expiry_date)}` : ' · No expiry'}
                    </div>
                  </div>
                ))}
              </HSection>

              <HSection label="Insurance Claims" count={(history.insuranceClaims||[]).length} empty="No insurance claims">
                {(history.insuranceClaims||[]).map((c, i) => (
                  <div key={c.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(6,182,212,0.05)',border:'1px solid rgba(6,182,212,0.15)'}}>
                    <div className="flex items-center justify-between gap-2">
                      <span style={{color:'#e2e8f0'}}>Informed {fmtD(c.informed_date)}{c.informed_person ? ` — ${c.informed_person}` : ''}</span>
                      <Badge value={c.status}/>
                    </div>
                    <div className="mt-1" style={{color:'#94a3b8'}}>
                      {c.claim_received ? `Claim received: ${fmtLKR(c.claim_received_amount)}` : 'Claim not yet received'}
                    </div>
                    {c.remarks && <div className="mt-1" style={{color:'#334155'}}>{c.remarks}</div>}
                  </div>
                ))}
              </HSection>

              {(history.donations||[]).length > 0 && (
                <HSection label="Donation" count={history.donations.length} empty="">
                  {history.donations.map((d, i) => (
                    <div key={d.id||i} className="p-3 rounded-xl mb-2 text-xs" style={{background:'rgba(168,85,247,0.05)',border:'1px solid rgba(168,85,247,0.15)'}}>
                      <div style={{color:'#e2e8f0'}}>Donated to <strong>{d.donated_to||'—'}</strong> on {fmtD(d.donated_date)}</div>
                      <div className="mt-1" style={{color:'#94a3b8'}}>{d.approved_by ? `Approved by ${d.approved_by}` : ''}</div>
                      {d.reason && <div className="mt-1" style={{color:'#334155'}}>{d.reason}</div>}
                      {d.remarks && <div className="mt-1" style={{color:'#334155'}}>{d.remarks}</div>}
                    </div>
                  ))}
                </HSection>
              )}

              <HSection label="Change Log" count={(history.audit||[]).length} empty="No audit entries">
                <div className="rounded-xl overflow-hidden" style={{border:'1px solid rgba(255,255,255,0.06)'}}>
                  {(history.audit||[]).map((x, i) => (
                    <div key={x.id||i} className="flex items-start gap-2 px-3 py-2 text-xs"
                      style={{background: i % 2 ? 'rgba(255,255,255,0.02)' : 'transparent'}}>
                      <span className="flex-shrink-0" style={{color:'#475569',minWidth:118}}>
                        {new Date(x.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'2-digit',hour:'2-digit',minute:'2-digit'})}
                      </span>
                      <span className="flex-shrink-0" style={{color:'#60a5fa',minWidth:52}}>{cap(x.action)}</span>
                      <span className="flex-shrink-0" style={{color:'#94a3b8',minWidth:110}}>{x.user_name||x.username||'—'}</span>
                      <span style={{color:'#334155'}}>{x.description||'—'}</span>
                    </div>
                  ))}
                </div>
              </HSection>

              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── VIEW MODAL ──────────────────────────────────────── */}
      {modal === 'view' && viewAsset && (
        <Modal title={`Asset Details — ${viewAsset.asset_number}`} onClose={() => { setModal(null); setViewAsset(null); }} size="xl">
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            {/* Core Info */}
            <div className="grid grid-cols-3 gap-3 text-xs">
              {[
                ['Asset #', viewAsset.asset_number],
                ['Category', viewAsset.category?.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase())],
                ['Status', <Badge value={viewAsset.status}/>],
                ['Brand', viewAsset.brand||'—'],
                ['Model', viewAsset.model||'—'],
                ['Serial No', viewAsset.serial_number||'—'],
                ['IMEI 1', viewAsset.imei_1||'—'],
                ['IMEI 2', viewAsset.imei_2||'—'],
                ['MAC Address', viewAsset.mac_address||'—'],
                ['IP Address', viewAsset.ip_address||'—'],
                ['Company', viewAsset.Company?.name||'—'],
                ['Department', viewAsset.Department?.name||'—'],
                ['Current Holder', viewAsset.currentHolder?.full_name||'Unassigned'],
                ['Purchase Date', viewAsset.purchased_date||'—'],
                ['Purchase Price', viewAsset.purchase_price ? `LKR ${Number(viewAsset.purchase_price).toLocaleString()}` : '—'],
                ['Vendor', viewAsset.vendor||'—'],
              ].map(([k,v]) => (
                <div key={k} className="rounded-lg p-2.5" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{color:'#475569',fontSize:10,marginBottom:2}}>{k}</div>
                  <div style={{color:'#e2e8f0',fontWeight:600}}>{v}</div>
                </div>
              ))}
            </div>

            {/* Warranty & Depreciation */}
            <div className="rounded-xl p-4" style={{background:'rgba(59,130,246,0.05)',border:'1px solid rgba(59,130,246,0.15)'}}>
              <div className="text-xs font-semibold mb-3" style={{color:'#60a5fa'}}>Warranty & Depreciation</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                {[
                  ['Warranty', (WARRANTY_PERIODS.find(p => p.value === viewAsset.warranty_period)?.label) || viewAsset.warranty_period || '—'],
                  ['Warranty Expiry', viewAsset.warranty_period === 'warranty_void' ? 'Voided' : (viewAsset.warranty_expiry_date||'—')],
                  ['Depreciation Status', viewAsset.depreciation_status||'—'],
                  ['Age', viewAsset.age_days != null ? `${Math.floor(viewAsset.age_days/365)}y ${Math.floor((viewAsset.age_days%365)/30)}mo (${viewAsset.age_days}d)` : '—'],
                  ['WDV', viewAsset.wdv != null ? `LKR ${Number(viewAsset.wdv).toLocaleString()}` : '—'],
                  ['Depreciated Value', viewAsset.depreciated_value != null ? `LKR ${Number(viewAsset.depreciated_value).toLocaleString()}` : '—'],
                ].map(([k,v]) => (
                  <div key={k} className="rounded-lg p-2.5" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                    <div style={{color:'#475569',fontSize:10,marginBottom:2}}>{k}</div>
                    <div style={{color:'#e2e8f0',fontWeight:600}}>{v}</div>
                  </div>
                ))}
              </div>
              {viewAsset.warranty_period === 'warranty_void' && (
                <div className="mt-3 rounded-lg p-2.5 text-xs" style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.2)'}}>
                  <span style={{color:'#fbbf24',fontWeight:600}}>Warranty voided</span>
                  <span style={{color:'#94a3b8'}}>
                    {viewAsset.warranty_void_reason
                      ? ` — ${viewAsset.warranty_void_reason}`
                      : ' — no reason recorded. Edit the asset to add one.'}
                  </span>
                </div>
              )}
            </div>

            {/* Computer Details */}
            {viewAsset.computerDetail && (
              <div className="rounded-xl p-4" style={{background:'rgba(99,102,241,0.05)',border:'1px solid rgba(99,102,241,0.2)'}}>
                <div className="text-xs font-semibold mb-3" style={{color:'#818cf8'}}>💻 Computer Details</div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  {[
                    ['OS / Windows Version', viewAsset.computerDetail.windows_version||'—'],
                    ['Windows License Key', viewAsset.computerDetail.windows_license_key||'—'],
                    ['Office Version', viewAsset.computerDetail.office_version||'—'],
                    ['Office License Key', viewAsset.computerDetail.office_license_key||'—'],
                    ['RAM', viewAsset.computerDetail.ram||'—'],
                    ['Storage', viewAsset.computerDetail.storage||'—'],
                    ['Processor', viewAsset.computerDetail.processor||'—'],
                    ['Antivirus', viewAsset.computerDetail.antivirus||'—'],
                    ['Login Username', viewAsset.computerDetail.username||'—'],
                  ].map(([k,v]) => (
                    <div key={k} className="rounded-lg p-2.5" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <div style={{color:'#475569',fontSize:10,marginBottom:2}}>{k}</div>
                      <div style={{color:'#e2e8f0',fontFamily:k.includes('Key')?'monospace':'inherit',fontWeight:600,wordBreak:'break-all'}}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Derecognition */}
            {viewAsset.derecognized && (
              <div className="rounded-xl p-4" style={{background:'rgba(239,68,68,0.05)',border:'1px solid rgba(239,68,68,0.2)'}}>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-semibold" style={{color:'#f87171'}}>Derecognized / Disposed</div>
                  <div className="flex items-center gap-2">
                    {viewAsset.derecognition_type === 'sold' && canEditSale && viewAsset.purchaser_name && (
                      <button
                        onClick={() => {
                          const win = window.open('', '_blank');
                          if (win) win.location.href = `/api/assets/${viewAsset.id}/sale-letter?token=${localStorage.getItem('token')}`;
                        }}
                        className="btn-ghost text-xs px-2.5 py-1 flex items-center gap-1.5"
                        style={{color:'#60a5fa',borderColor:'rgba(96,165,250,0.3)'}}>
                        <Receipt size={11}/>Print Sale Letter
                      </button>
                    )}
                    {viewAsset.derecognition_type === 'sold' && canEditSale && (
                      <button
                        onClick={() => {
                          setSaleForm({
                            purchaser_name: viewAsset.purchaser_name || '',
                            purchaser_id_number: viewAsset.purchaser_id_number || '',
                            purchaser_address: viewAsset.purchaser_address || '',
                            purchaser_contact: viewAsset.purchaser_contact || '',
                          });
                          setSaleError('');
                          setShowSaleLetter(true);
                        }}
                        className="btn-ghost text-xs px-2.5 py-1 flex items-center gap-1.5"
                        style={{color:'#34d399',borderColor:'rgba(52,211,153,0.3)'}}>
                        <Receipt size={11}/>{viewAsset.purchaser_name ? 'Edit Details' : 'Enter Purchaser Details'}
                      </button>
                    )}
                    {viewAsset.derecognition_type === 'sold' && isIT && !canEditSale && (
                      viewAsset.purchaser_name ? (
                        <button
                          onClick={() => {
                            const win = window.open('', '_blank');
                            if (win) win.location.href = `/api/assets/${viewAsset.id}/sale-letter?token=${localStorage.getItem('token')}`;
                          }}
                          className="btn-ghost text-xs px-2.5 py-1 flex items-center gap-1.5"
                          style={{color:'#60a5fa',borderColor:'rgba(96,165,250,0.3)'}}>
                          <Receipt size={11}/>Print Sale Letter
                        </button>
                      ) : (
                        <span className="text-xs" style={{color:'#64748b'}}>Awaiting IT to enter purchaser details</span>
                      )
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3 text-xs">
                  {[
                    ['Type', viewAsset.derecognition_type||'—'],
                    ['Date', viewAsset.derecognition_date||'—'],
                    ['Approved By', viewAsset.derecognition_approved_by||'—'],
                    ['Sale Value', viewAsset.sale_value ? `LKR ${Number(viewAsset.sale_value).toLocaleString()}` : '—'],
                  ].map(([k,v]) => (
                    <div key={k} className="rounded-lg p-2.5" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                      <div style={{color:'#475569',fontSize:10,marginBottom:2}}>{k}</div>
                      <div style={{color:'#e2e8f0',fontWeight:600}}>{v}</div>
                    </div>
                  ))}
                  {/* Purchaser summary if already saved */}
                  {viewAsset.purchaser_name && (
                    <div className="col-span-3 rounded-lg p-2.5" style={{background:'rgba(52,211,153,0.05)',border:'1px solid rgba(52,211,153,0.15)'}}>
                      <div style={{color:'#475569',fontSize:10,marginBottom:2}}>Purchaser</div>
                      <div style={{color:'#34d399',fontWeight:600}}>{viewAsset.purchaser_name} · {viewAsset.purchaser_id_number||'—'} · {viewAsset.purchaser_contact||'—'}</div>
                      {viewAsset.purchaser_address && <div className="mt-1" style={{color:'#475569'}}>{viewAsset.purchaser_address}</div>}
                    </div>
                  )}
                  {/* Donation recipient details, if this asset was donated */}
                  {viewAsset.derecognition_type === 'donated' && (
                    <div className="col-span-3 rounded-lg p-2.5" style={{background:'rgba(168,85,247,0.05)',border:'1px solid rgba(168,85,247,0.15)'}}>
                      <div style={{color:'#475569',fontSize:10,marginBottom:2}}>Donated To</div>
                      {viewDonation ? (
                        <>
                          <div style={{color:'#c084fc',fontWeight:600}}>{viewDonation.donated_to}</div>
                          {viewDonation.reason && <div className="mt-1" style={{color:'#475569'}}>Reason: {viewDonation.reason}</div>}
                          {viewDonation.remarks && <div className="mt-1" style={{color:'#475569'}}>{viewDonation.remarks}</div>}
                        </>
                      ) : <div style={{color:'#475569'}}>Loading…</div>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {viewAsset.remarks && (
              <div className="rounded-xl p-3 text-xs" style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.06)'}}>
                <span style={{color:'#475569'}}>Remarks: </span><span style={{color:'#94a3b8'}}>{viewAsset.remarks}</span>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Sale Letter — Purchaser Details Modal */}
      {viewAsset && showSaleLetter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{background:'rgba(4,8,16,0.92)',backdropFilter:'blur(12px)'}}>
          <div className="relative w-full max-w-lg rounded-2xl overflow-hidden" style={{background:'linear-gradient(135deg,#0d1528,#080f1e)',border:'1px solid rgba(52,211,153,0.3)'}}>
            <div className="flex items-center justify-between px-5 py-4" style={{borderBottom:'1px solid rgba(255,255,255,0.07)',background:'rgba(52,211,153,0.06)'}}>
              <div className="flex items-center gap-3">
                <div style={{width:34,height:34,borderRadius:10,background:'rgba(52,211,153,0.12)',border:'1px solid rgba(52,211,153,0.25)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Receipt size={15} style={{color:'#34d399'}}/>
                </div>
                <div>
                  <div className="font-semibold text-sm" style={{color:'#e2e8f0'}}>Generate Sale Letter</div>
                  <div className="text-xs" style={{color:'#475569'}}>Asset {viewAsset.asset_number} · Enter purchaser details</div>
                </div>
              </div>
              <button onClick={() => setShowSaleLetter(false)} style={{background:'none',border:'none',cursor:'pointer',color:'#64748b'}}><X size={14}/></button>
            </div>
            <div className="p-5 space-y-4">
              {saleError && <div className="p-3 rounded-xl text-xs" style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',color:'#f87171'}}>{saleError}</div>}

              <div className="p-3 rounded-xl text-xs" style={{background:'rgba(59,130,246,0.06)',border:'1px solid rgba(59,130,246,0.15)',color:'#60a5fa'}}>
                These details will appear on the official sale &amp; liability transfer letter. Ensure they match the buyer's ID exactly.
              </div>

              {[
                {label:'Full Name *', key:'purchaser_name', placeholder:'As per National ID / Passport'},
                {label:'NIC / Passport / ID No. *', key:'purchaser_id_number', placeholder:'e.g. 199812345678 or N1234567'},
                {label:'Contact Number *', key:'purchaser_contact', placeholder:'e.g. 0771234567'},
              ].map(({label,key,placeholder}) => (
                <div key={key}>
                  <label className="block text-xs mb-1" style={{color:'#94a3b8'}}>{label}</label>
                  <input
                    className="input-dark w-full text-sm"
                    placeholder={placeholder}
                    value={saleForm[key]}
                    onChange={e => setSaleForm(f => ({...f, [key]: e.target.value}))}
                  />
                </div>
              ))}
              <div>
                <label className="block text-xs mb-1" style={{color:'#94a3b8'}}>Address *</label>
                <textarea
                  className="input-dark w-full text-sm"
                  rows={3}
                  placeholder="Full residential address"
                  value={saleForm.purchaser_address}
                  onChange={e => setSaleForm(f => ({...f, purchaser_address: e.target.value}))}
                  style={{resize:'vertical'}}
                />
              </div>
            </div>
            <div className="flex gap-2 px-5 pb-5">
              <button onClick={() => setShowSaleLetter(false)} className="btn-ghost flex-1 text-sm">Cancel</button>
              <button
                disabled={saleSaving}
                className="btn-primary flex-1 text-sm flex items-center justify-center gap-2"
                onClick={async () => {
                  if (!saleForm.purchaser_name.trim() || !saleForm.purchaser_id_number.trim() || !saleForm.purchaser_contact.trim() || !saleForm.purchaser_address.trim()) {
                    setSaleError('All fields are required.');
                    return;
                  }
                  // Open window before await — popup blockers block window.open in async context
                  const letterWin = window.open('', '_blank');
                  setSaleSaving(true);
                  setSaleError('');
                  try {
                    await api.post(`/assets/${viewAsset.id}/sale-details`, saleForm);
                    const r = await api.get(`/assets/${viewAsset.id}`);
                    setViewAsset(r.data);
                    if (letterWin) letterWin.location.href = `/api/assets/${viewAsset.id}/sale-letter?token=${localStorage.getItem('token')}`;
                    setShowSaleLetter(false);
                  } catch (e) {
                    setSaleError(e.response?.data?.error || 'Failed to save. Please try again.');
                  } finally { setSaleSaving(false); }
                }}>
                <Receipt size={13}/>
                {saleSaving ? 'Saving...' : 'Save & Generate Letter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
